import React, { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { SortingState } from '@tanstack/react-table'
import {
  GET_PRICELIST,
  GET_PRICELIST_PRODUCTS,
  GET_PRICELIST_SOFA_MODULES,
  GET_PRICELIST_COMPONENTS,
} from '../priceListQueries'
import { useLanguage } from '@/context/LanguageContext'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContainer } from '@/components/shared/page-container'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { DataTable } from '@/components/shared/data-table/data-table'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { ColumnDef } from '@tanstack/react-table'
import {
  columns as defaultSofaModuleColumns,
  SofaModule,
} from './sofa-modules-columns'
import { columns as productColumns } from './column_products'

interface SofaModulesTableProps {
  priceListId: number
  advancedProductId: number | null
}

interface AdditionalComponentsTableProps {
  priceListId: number
  advancedProductId: number | null
}

export type AdditionalComponent = {
  id: string
  code: string
  name: string
  price_fabric_category: Array<{
    price: number
    fabrice_price_category: {
      group_number: number
    }
  }>
  extra_prices: Array<{
    price: number
  }>
}

// Define columns for additional components table (matching Sofa Modules format)
const additionalComponentsColumns: ColumnDef<AdditionalComponent>[] = [
  {
    accessorKey: 'code',
    header: () => 'Code',
  },
  {
    accessorKey: 'name',
    header: () => 'Name',
  },
  {
    accessorKey: 'cat_1',
    header: () => <div className="text-right">CAT 1</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 1
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_2',
    header: () => <div className="text-right">CAT 2</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 2
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_3',
    header: () => <div className="text-right">CAT 3</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 3
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_4',
    header: () => <div className="text-right">CAT 4</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 4
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_5',
    header: () => <div className="text-right">CAT 5</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 5
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_6',
    header: () => <div className="text-right">CAT 6</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 6
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_7',
    header: () => <div className="text-right">CAT 7</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 7
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_L1',
    header: () => <div className="text-right">L1</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 11
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
  {
    accessorKey: 'cat_L2',
    header: () => <div className="text-right">L2</div>,
    cell: ({ row }) => {
      const price =
        row.original.price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 12
        )?.price || '-'
      return <div className="text-right">{price}</div>
    },
  },
]

// Define columns for extra prices table (simple format)
const extraPricesColumns: ColumnDef<any>[] = [
  {
    accessorKey: 'code',
    header: () => 'Code',
  },
  {
    accessorKey: 'name',
    header: () => 'Name',
  },
  {
    accessorKey: 'price',
    header: () => <div className="text-right">Price</div>,
    cell: ({ row }) => (
      <div className="text-right">{row.getValue('price')}</div>
    ),
  },
]

function AdditionalComponentsTable({
  priceListId,
  advancedProductId,
}: AdditionalComponentsTableProps) {
  const { data, loading, error } = useQuery(GET_PRICELIST_COMPONENTS, {
    variables: {
      id: priceListId,
      advancedProductId: advancedProductId,
    },
  })

  if (loading) return <div>Loading components...</div>
  if (error) return <div>Error loading components: {error.message}</div>

  const rawComponents = data?.findManyAdditionalComponentToAdvancedProduct || []

  // Separate components by pricing type
  const categoryComponents: AdditionalComponent[] = []
  const extraPriceComponents: any[] = []

  rawComponents.forEach((component: any) => {
    const componentName =
      component.additional_component?.additional_component_profiles?.[0]
        ?.name || 'Unnamed Component'
    const componentCode = component.additional_component?.code || '-'
    const componentId = component.additional_component?.id || 'unknown'

    // Components with fabric category pricing
    if (
      component.price_fabric_category &&
      component.price_fabric_category.length > 0
    ) {
      categoryComponents.push({
        id: componentId,
        code: componentCode,
        name: componentName,
        price_fabric_category: component.price_fabric_category || [],
        extra_prices: [],
      })
    }

    // Components with extra pricing (no categories)
    if (component.extra_prices && component.extra_prices.length > 0) {
      component.extra_prices.forEach((extraPrice: any, index: number) => {
        extraPriceComponents.push({
          id: `${componentId}_extra_${index}`,
          code: componentCode,
          name: componentName,
          price: extraPrice.price || '-',
        })
      })
    }
  })

  if (categoryComponents.length === 0 && extraPriceComponents.length === 0) {
    return (
      <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-md">
        No additional components found for this price list.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {categoryComponents.length > 0 && (
        <div>
          <h4 className="mb-3 text-sm font-medium text-gray-700">
            Category Pricing
          </h4>
          <DataTable
            columns={additionalComponentsColumns}
            data={categoryComponents}
            defaultPageSize={10}
          />
        </div>
      )}

      {extraPriceComponents.length > 0 && (
        <div>
          <h4 className="mb-3 text-sm font-medium text-gray-700">
            Extra Pricing
          </h4>
          <DataTable
            columns={extraPricesColumns}
            data={extraPriceComponents}
            defaultPageSize={10}
          />
        </div>
      )}
    </div>
  )
}

function SofaModulesTable({
  priceListId,
  advancedProductId,
}: SofaModulesTableProps) {
  console.log(priceListId, advancedProductId)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })
  const [columnFilters, setColumnFilters] = useState([])
  const [sorting, setSorting] = useState<SortingState>([])

  const orderBy = sorting.map((sort: any) => ({
    [sort.id]: sort.desc ? 'desc' : 'asc',
  }))

  const { data, loading } = useQuery(GET_PRICELIST_SOFA_MODULES, {
    variables: {
      id: priceListId,
      advancedProductId: advancedProductId,
      skip: pagination.pageIndex * pagination.pageSize,
      take: pagination.pageSize,
      orderBy,
      searchTerm: '',
    },
  })

  const totalCount = data?.findManySofaFormCount || 0
  const sofaModules = (data?.findManySofaForm || []) as SofaModule[]

  return (
    <DataTableManual<SofaModule, unknown>
      columns={defaultSofaModuleColumns}
      data={sofaModules}
      pageIndex={pagination.pageIndex}
      pageSize={pagination.pageSize}
      onPaginationChange={setPagination}
      onColumnFiltersChange={setColumnFilters}
      onSortingChange={setSorting}
      rowCount={totalCount}
    />
  )
}

export default function PriceListDetails() {
  const { id } = useParams()
  const {
    data: priceListData,
    loading: loadingDetails,
    error: detailsError,
  } = useQuery(GET_PRICELIST, {
    variables: { id: parseInt(id || '0', 10) },
    skip: !id,
  })
  const [productColumnFilters, setProductColumnFilters] = useState([])
  const [productSorting, setProductSorting] = useState<SortingState>([])
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  )
  const [productPagination, setProductPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })
  const { language } = useLanguage()
  const {
    data: productsData,
    loading: loadingProducts,
    error: productsError,
  } = useQuery(GET_PRICELIST_PRODUCTS, {
    variables: {
      id: parseInt(id || '0', 10),
      skip: productPagination.pageIndex * productPagination.pageSize,
      take: productPagination.pageSize,
      language,
    },
    skip: !id,
  })

  console.log('>>>', productPagination)

  if (loadingDetails || loadingProducts)
    return <div data-testid="loading">Loading...</div>
  if (detailsError?.message)
    return <div>Error loading details: {detailsError.message}</div>
  if (productsError?.message)
    return <div>Error loading products: {productsError.message}</div>
  if (!priceListData?.findUniquePriceList)
    return <div>Price list not found</div>

  const priceList = priceListData.findUniquePriceList
  const rawProducts = productsData?.findManyProductContainer || []
  const products = rawProducts.map((product: any) => ({
    id: product.id,
    container_id: product.id,
    advanced_product_id: product.advanced_product?.id || null,
    type: product.type,
    name:
      product.advanced_product?.advanced_product_profiles?.[0]?.name ||
      product.single_product?.product_profiles?.[0]?.name ||
      'Unnamed Product',
    basePrice: product.advanced_product?.base_prices?.[0]?.price || '-',
    priceFrom: product.advanced_product?.base_prices?.[0]?.price_from || '-',
  }))
  const totalCount = productsData?.findManyProductContainerCount || 0
  const selectedProduct = productsData.findManyProductContainer.find(
    p => p.id === selectedProductId
  )

  return (
    <PageContainer>
      <HeaderSection title={`Price List: ${priceList.name}`} />

      <DataTableManual
        columns={productColumns}
        data={products}
        pageIndex={productPagination.pageIndex}
        pageSize={productPagination.pageSize}
        onPaginationChange={setProductPagination}
        onColumnFiltersChange={setProductColumnFilters}
        onSortingChange={setProductSorting}
        rowCount={totalCount}
        onRowClick={row => {
          setSelectedProductId(selectedProductId === row.id ? null : row.id)
        }}
        className="[&_tr:hover]:cursor-pointer"
      />

      {selectedProductId && (
        <div className="mt-4 space-y-4">
          <div className="p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              Selected Product ID: {selectedProductId}
            </p>
          </div>

          <div>
            <h3 className="mb-3 font-semibold">Additional Components</h3>
            <AdditionalComponentsTable
              priceListId={parseInt(id || '0', 10)}
              advancedProductId={selectedProduct?.advanced_product?.id || null}
            />
          </div>
        </div>
      )}

      {rawProducts.some(p => p.advanced_product?.sofa_forms?.length > 0) && (
        <div className="mt-8">
          <h2 className="mb-4 font-semibold">Sofa Modules</h2>
          <SofaModulesTable
            priceListId={parseInt(id || '0', 10)}
            advancedProductId={selectedProduct?.advanced_product?.id}
          />
        </div>
      )}
    </PageContainer>
  )
}
